import * as React from 'react';
import { useState } from 'react';
import { HotelFormConnected, RingsSpinner, RatingProps, WSAppContext, hotelUtils } from '@weddingspot/ws-core-components';
import * as qs from 'query-string';
import axios from 'axios';
import { pickBy, some, fromPairs } from 'lodash-es';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { logEvent, AnalyticsEvent } from '@weddingspot/ws-analytics';
import { CSN_VENUE_PROFILE_URL } from '../../constants/config';
import { CSNVenueRating } from '@weddingspot/ws-api-client';

const styles = require('./styles.module.scss');

enum Step {
    HOTEL,
    RFP,
    SUCCESS,
}

const Success = (props: { onClose: () => void; onDone: () => void }) => {
    return (
        <div className={styles.success}>
            Your request is sent! Cvent will send updates to the email address you provided.
            <div className={styles.buttonsRow}>
                <button className='nx-button nx-button--second' type='button' onClick={props.onClose}>
                    No, Thanks
                </button>
                <button className='InquiryWidget--success--cta nx-button nx-button--primary' onClick={props.onDone}>
                    Explore Venues
                </button>
            </div>
        </div>
    );
};

const HotelRecommendetionCell = (props: HotelData & { isSelected: boolean; onSelect: (newValue: boolean) => void }) => {
    return (
        <div className={styles.hotel}>
            {props.image ? (
                <div className={styles.image} style={{ backgroundImage: `url(${props.image})` }} />
            ) : (
                <div className={styles.image}>
                    <RingsSpinner animate={false} />
                </div>
            )}
            <div className={styles.name}>{props.name}</div>
            <div className={styles.city}>
                {props.city}, {props.state}
            </div>
            <label className={styles.checkbox}>
                <input
                    type='checkbox'
                    className='MaterialCheckboxInput--input'
                    checked={props.isSelected}
                    onChange={() => props.onSelect(!props.isSelected)}
                />
                Get Pricing
            </label>
        </div>
    );
};

type HotelData = {
    image: string;
    name: string;
    city: string;
    state?: string;
    rating?: string;
    uid: string;
};

type SelectedHotel = {
    [index: string]: boolean;
};

type HotelComponentType = {
    data: HotelData[];
    onNext: () => void;
    selectedHotel: SelectedHotel;
    setSelectedHotel: (newValue: SelectedHotel) => void;
    onClose: () => void;
};

const HotelComponent = (props: HotelComponentType) => {
    const isSomeHotelSelected = some(props.selectedHotel);
    const isLoaded = props.data.length;

    if (!isLoaded) {
        return <RingsSpinner animate={true} />;
    }
    return (
        <div className={styles.hotelComponent}>
            <div>Explore these hotels to find the best rooms for your guests</div>
            <div className={styles.hotels}>
                {props.data.map((hotel) => {
                    const isSelected = props.selectedHotel[hotel.uid];
                    return (
                        <HotelRecommendetionCell
                            {...hotel}
                            isSelected={isSelected}
                            onSelect={(newValue) =>
                                props.setSelectedHotel({
                                    ...props.selectedHotel,
                                    [hotel.uid]: newValue,
                                })
                            }
                        />
                    );
                })}
            </div>

            <div className={styles.buttonsRow}>
                <button className='nx-button nx-button--second' type='button' onClick={props.onClose}>
                    Cancel{' '}
                </button>
                <button disabled={!isSomeHotelSelected} onClick={props.onNext} className='nx-button nx-button--primary'>
                    Next
                </button>
            </div>
        </div>
    );
};

type HotelAPIObj = {
    basicProfile: {
        name: string;
        city: string;
        stateProvinceCode?: string;
        heroImage?: {
            extraSmall?: string;
        };
    };
    preferredRating?: CSNVenueRating;
    images: Array<any>;
    ofrgId: string;
};

const handleRoomCountSubmit = (props: object) => {
    logEvent(AnalyticsEvent.EVTNM_SUBMITTED_ROOM_COUNT, props);
};

const handleClickedSpecifyGuestRooms = () => {
    logEvent(AnalyticsEvent.EVTNM_CLICKED_SPECIFY_GUEST_ROOMS, { Source: 'RFP email' });
};

export const RFPEmailContainer = (props: RouteComponentProps) => {
    const [step, setStep] = useState<Step>(Step.HOTEL);
    const [hotelData, setHotelData] = useState<HotelData[]>([]);
    const [selectedHotel, setSelectedHotel] = useState<SelectedHotel>({});
    const { user } = React.useContext(WSAppContext);
    const history = useHistory();

    const parsedQS = qs.parse(props.location.search) as { uids: string };
    const HotelUids = parsedQS.uids.split(',');

    React.useEffect(() => {
        logEvent(AnalyticsEvent.EVTNM_VISITED_RFP_EMAIL, { source: 'guest rooms follow up email' });

        axios.post<Array<HotelAPIObj>>(CSN_VENUE_PROFILE_URL, HotelUids).then(({ data }: { data: HotelAPIObj[] }) => {
            setHotelData(
                data.map((hotel) => {
                    const image = hotel.images.find((item) => item.group === 'EXTERIOR')?.imageURL?.extraSmall;
                    return {
                        name: hotel.basicProfile.name,
                        city: hotel.basicProfile.city,
                        state: hotel.basicProfile.stateProvinceCode,
                        rating: hotel.preferredRating,
                        image: image || hotel.basicProfile?.heroImage?.extraSmall,
                        uid: hotel.ofrgId,
                    };
                })
            );
            setSelectedHotel(fromPairs(data.map((hotel) => [hotel.ofrgId, true])));
        });
    }, []);

    const csnVenueData = Object.entries(pickBy(selectedHotel)).map(([key]) => {
        let _hotelData = hotelData.find((h) => h.uid === key);

        let _ratingProps = hotelUtils.getHotelRatingFromCsnFormat(_hotelData?.rating);
        let _rating = !!_ratingProps.value && !!_ratingProps.type ? (_ratingProps as RatingProps) : undefined;

        let _meta = _hotelData
            ? {
                  name: _hotelData.name,
                  location: `${_hotelData.city}, ${_hotelData.state}`,
                  previewImg: _hotelData.image,
                  rating: _rating,
              }
            : undefined;

        return {
            id: key,
            meta: _meta,
        };
    });

    return (
        <div className={styles.wrapper}>
            <div className={styles.popup}>
                <header className={styles.header}>
                    {step === Step.HOTEL && 'Explore our best hotel room blocks recommendations'}
                    {step === Step.RFP && 'Room Block Reservation'}
                    {step === Step.SUCCESS && 'Success'}
                </header>
                <div className={styles.innerWrapper}>
                    {step === Step.HOTEL && (
                        <HotelComponent
                            data={hotelData}
                            onNext={() => setStep(Step.RFP)}
                            selectedHotel={selectedHotel}
                            setSelectedHotel={setSelectedHotel}
                            onClose={() => history.push('/wedding-venues/')}
                        />
                    )}
                    {step === Step.RFP && (
                        <HotelFormConnected
                            type='venueProfile'
                            onClose={() => history.push('/wedding-venues/')}
                            onSuccess={() => {
                                setStep(Step.SUCCESS);
                                logEvent(AnalyticsEvent.EVTNM_SUBMITTED_RFP_EMAIL, { source: 'guest rooms follow up email' });
                            }}
                            handleRoomCountSubmit={handleRoomCountSubmit}
                            handleClickedSpecifyGuestRooms={handleClickedSpecifyGuestRooms}
                            csnVenueData={csnVenueData}
                            defaultFormValue={user || undefined}
                            leadSource='post outreach'
                        />
                    )}
                    {step === Step.SUCCESS && (
                        <Success onClose={() => history.push('/wedding-venues/')} onDone={() => history.push('/wedding-venues/')} />
                    )}
                </div>
            </div>
        </div>
    );
};
